import { useState, useEffect, useCallback, useRef } from 'react';

import {
  findScrollContainer,
  findHeaderElement,
  calculateScrollPosition,
  calculateTailGap,
} from '../PhotoDamageAnalyzer/utils';

export interface MessagePair {
  user: any;
  assistant: any | null;
  userIndex: number;
  assistantIndex: number | null;
}

interface UseTailGapParams {
  scrollRootRef: React.RefObject<HTMLDivElement | null>;
  messageRefs: React.RefObject<Record<number, HTMLDivElement | null>>;
  gapRef: React.RefObject<HTMLDivElement | null>;
  typingRef: React.RefObject<HTMLDivElement | null>;
  pairs: MessagePair[];
  loadingMessage: string;
  hasError: boolean;
  maxTailGap: number;
  featureTitle: string;
}

/**
 * Custom hook to calculate and manage the tail gap
 * The tail gap is the space at the bottom to keep the last message anchored properly
 */
export const useTailGap = ({
  scrollRootRef,
  messageRefs,
  gapRef,
  typingRef,
  pairs,
  loadingMessage,
  hasError,
  maxTailGap,
  featureTitle,
}: UseTailGapParams) => {
  const [tailGap, setTailGap] = useState(0);
  const resizeObsRef = useRef<ResizeObserver | null>(null);
  const lastMeasuredScrollHeight = useRef<number>(0);
  const prevPairsCountRef = useRef<number>(0);

  const adjustScrollGap = useCallback(() => {
    const root = scrollRootRef.current;
    if (!root || pairs.length === 0 || !messageRefs.current) {
      return;
    }

    const lastPair = pairs[pairs.length - 1];
    const userElement = messageRefs.current[lastPair.userIndex] || null;
    const assistantElement =
      lastPair.assistantIndex !== null
        ? messageRefs.current[lastPair.assistantIndex!]
        : null;

    const userHeight = userElement?.getBoundingClientRect().height ?? 0;
    const assistantHeight =
      assistantElement?.getBoundingClientRect().height ?? null;
    const gapHeight = gapRef.current?.getBoundingClientRect().height ?? 0;
    const typingHeight =
      typingRef.current?.getBoundingClientRect().height ?? null;
    const isLoading = loadingMessage !== '';

    const gap = hasError
      ? 0
      : calculateTailGap(
          root.clientHeight,
          userHeight,
          assistantHeight,
          gapHeight,
          typingHeight,
          isLoading,
          maxTailGap,
        );

    setTailGap(gap);
  }, [pairs, loadingMessage, hasError, maxTailGap]);

  // ResizeObserver to keep layout consistent
  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root || pairs.length === 0 || !messageRefs.current) {
      return;
    }

    // Detect new pair by counting
    prevPairsCountRef.current = pairs.length;

    // Last pair elements already mounted
    const lastPair = pairs[pairs.length - 1];
    const userElement = messageRefs.current[lastPair.userIndex] || null;
    const assistantElement =
      lastPair.assistantIndex !== null
        ? messageRefs.current[lastPair.assistantIndex!]
        : null;
    const typingElement =
      lastPair.assistantIndex === null ? typingRef.current : null;

    if (resizeObsRef.current) {
      resizeObsRef.current.disconnect();
    }

    const resizeObserver = new ResizeObserver(() => {
      // Let browser apply layout changes first
      requestAnimationFrame(() => {
        adjustScrollGap();

        // if the last pair still doesn't have an assistant response,
        // reapply the same anchor as for the messages so that user + typing
        // stay in the same visual zone.
        if (lastPair.assistantIndex === null && typingRef.current) {
          const scrollContainer = findScrollContainer(root);
          if (!scrollContainer) {
            return;
          }

          const headerElement = findHeaderElement(
            featureTitle,
            scrollContainer,
          );

          const anchorElement =
            messageRefs.current?.[lastPair.userIndex] || typingRef.current;

          const scrollPosition = calculateScrollPosition(
            scrollContainer,
            headerElement,
            anchorElement,
          );

          scrollContainer.scrollTop = scrollPosition;
          scrollContainer.scrollTo({ top: scrollPosition });
        }
      });
    });

    resizeObserver.observe(root);
    if (userElement) {
      resizeObserver.observe(userElement);
    }
    if (assistantElement) {
      resizeObserver.observe(assistantElement);
    }
    if (!assistantElement && typingElement) {
      resizeObserver.observe(typingElement);
    }

    resizeObsRef.current = resizeObserver;

    // Initial calculation
    requestAnimationFrame(() => {
      adjustScrollGap();
      lastMeasuredScrollHeight.current = root.scrollHeight;
    });

    return () => resizeObserver.disconnect();
  }, [pairs, adjustScrollGap, featureTitle]);

  return tailGap;
};
